import { useState, useEffect } from "react";
import api from "../lib/api";
import {
  DollarSign, Clock, TrendingUp, Users, MapPin, ChevronDown, ChevronUp,
  Target, Zap, ArrowUpRight, Settings2, Check, X, Plus, Trash2, FileText, Printer
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import StaffTimeClockLogs from "../components/StaffTimeClockLogs";

const TIER_COLORS = {
  "Tier 3": { bg: "#1A1A1A", text: "#FFFFFF", accent: "#1A1A1A" },
  "Tier 2": { bg: "#8B5CF6", text: "#FFFFFF", accent: "#8B5CF6" },
  "Tier 1": { bg: "#2D5936", text: "#FFFFFF", accent: "#2D5936" },
  "Base":   { bg: "#E6E4E0", text: "#5E5E5E", accent: "#8A8A8A" },
};

function HourlyCard({ p, startDate, endDate, onChanged }) {
  // Allow managers to expand a card and see/edit raw shift logs. Hourly
  // staff are the most common case for "I forgot to clock in" → add
  // manual shift, and for adding/removing breaks after the fact.
  const [showLogs, setShowLogs] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`hourly-card-${p.user_id}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ backgroundColor: p.color }}>
              {p.name?.[0]}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-400 capitalize">{p.role?.replace("_", " ")}</p>
            </div>
          </div>
          <span className="text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700" data-testid={`paytype-hourly-${p.user_id}`}>
            Hourly
          </span>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-xl p-4 mb-3">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-500">Clocked Hours × Rate</p>
              <p className="font-['Outfit'] text-2xl font-semibold text-slate-900 tabular-nums mt-0.5">
                {(p.clocked_hours ?? 0).toFixed(2)}h <span className="text-slate-400 text-base">×</span> ${Number(p.hourly_rate || 0).toFixed(2)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Gross</p>
              <p className="font-['Outfit'] text-3xl font-semibold text-indigo-600 tabular-nums">${(p.hourly_pay ?? 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div><p className="font-['Outfit'] text-base font-medium text-slate-900">{p.appointments}</p><p className="text-[10px] text-slate-400">Appts</p></div>
          <div><p className="font-['Outfit'] text-base font-medium text-emerald-600">${(p.tips ?? 0).toFixed(0)}</p><p className="text-[10px] text-slate-400">Tips</p></div>
          <div><p className="font-['Outfit'] text-base font-semibold text-slate-900">${(p.total_pay ?? 0).toFixed(2)}</p><p className="text-[10px] text-slate-400">Take-Home</p></div>
        </div>
        {(p.clocked_hours ?? 0) === 0 && (
          <p className="text-[11px] text-amber-600 mt-2 text-center font-['Manrope']" data-testid={`no-hours-${p.user_id}`}>
            No clocked hours in this period
          </p>
        )}

        <button onClick={() => setShowLogs(s => !s)} data-testid={`toggle-logs-${p.user_id}`}
          className="mt-3 w-full text-xs font-medium text-slate-600 hover:text-slate-900 py-1.5 rounded-lg hover:bg-slate-50">
          {showLogs ? "Hide shift logs" : "View / edit shift logs"}
        </button>
        {showLogs && (
          <StaffTimeClockLogs
            staffId={p.user_id}
            staffName={p.name}
            startDate={startDate}
            endDate={endDate}
            onChanged={onChanged}
          />
        )}
      </div>
    </div>
  );
}

function CommissionCard({ p, tiers, startDate, endDate, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const tierStyle = TIER_COLORS[p.commission_tier] || TIER_COLORS.Base;
  const maxTierSales = tiers.length > 0 ? Math.max(...tiers.map(t => t.min_sales)) : 1750;
  const progress = Math.min((p.week_sales / maxTierSales) * 100, 100);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`commission-card-${p.user_id}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ backgroundColor: p.color }}>
              {p.name?.[0]}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-400 capitalize">{p.role?.replace("_", " ")}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700" data-testid={`paytype-commission-${p.user_id}`}>
              Commission
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: tierStyle.bg, color: tierStyle.text }}>
              {p.commission_tier} — {(p.variable_rate * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        {/* 7-Day Sales Progress */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-slate-400">7-Day Service Sales</span>
            <span className="text-sm font-semibold text-slate-900">${p.week_sales.toLocaleString()}</span>
          </div>
          <div className="relative h-3 bg-[#F3F3F3] rounded-full overflow-hidden">
            {/* Tier markers */}
            {tiers.filter(t => t.min_sales > 0).map(t => (
              <div key={t.min_sales} className="absolute top-0 bottom-0 w-px bg-white z-10"
                style={{ left: `${(t.min_sales / maxTierSales) * 100}%` }} />
            ))}
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress}%`, backgroundColor: tierStyle.accent }} />
          </div>
          <div className="flex justify-between mt-1">
            {tiers.filter(t => t.min_sales > 0).map(t => (
              <span key={t.min_sales} className="text-[9px] text-slate-400" style={{ marginLeft: `${Math.max((t.min_sales / maxTierSales) * 100 - 5, 0)}%` }}>
                ${t.min_sales} ({(t.rate * 100).toFixed(0)}%)
              </span>
            ))}
          </div>
        </div>

        {/* Next tier nudge */}
        {p.next_tier && (
          <div className="bg-[#FDF3E1] rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-600">
              <span className="font-semibold">${p.next_tier.gap.toLocaleString()}</span> more to reach {p.next_tier.label} ({(p.next_tier.rate * 100).toFixed(0)}%)
            </p>
          </div>
        )}

        {/* Key numbers */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div><p className="font-['Outfit'] text-lg font-medium text-slate-900">${p.commission.toFixed(0)}</p><p className="text-[10px] text-slate-400">Commission</p></div>
          <div><p className="font-['Outfit'] text-lg font-medium text-slate-900">${p.service_revenue.toFixed(0)}</p><p className="text-[10px] text-slate-400">Revenue</p></div>
          <div><p className="font-['Outfit'] text-lg font-medium text-emerald-600">${p.tips.toFixed(0)}</p><p className="text-[10px] text-slate-400">Tips</p></div>
          <div><p className="font-['Outfit'] text-lg font-semibold text-slate-900">${p.total_pay.toFixed(0)}</p><p className="text-[10px] text-slate-400">Total</p></div>
        </div>

        {/* Expand toggle */}
        <button onClick={() => setExpanded(!expanded)} data-testid={`expand-${p.user_id}`}
          className="w-full mt-3 flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-slate-900 transition-colors py-1">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "Less" : "7-Day Breakdown"}
        </button>
        <button onClick={() => setShowLogs(s => !s)} data-testid={`toggle-logs-${p.user_id}`}
          className="mt-1 w-full text-xs font-medium text-slate-600 hover:text-slate-900 py-1.5 rounded-lg hover:bg-slate-50">
          {showLogs ? "Hide shift logs" : "View / edit shift logs"}
        </button>
        {showLogs && (
          <StaffTimeClockLogs
            staffId={p.user_id}
            staffName={p.name}
            startDate={startDate}
            endDate={endDate}
            onChanged={onChanged}
          />
        )}
      </div>

      {/* Expanded daily breakdown */}
      {expanded && (
        <div className="border-t border-slate-200 px-5 py-4 bg-[#FAFAFA]" data-testid={`daily-breakdown-${p.user_id}`}>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Daily Service Sales (7-Day Window)</h4>
          <div className="h-[120px] mb-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={p.daily_sales || []}>
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#8A8A8A" }} />
                <YAxis tickFormatter={v => `$${v}`} axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#8A8A8A" }} width={45} />
                <Tooltip formatter={v => [`$${v}`, "Revenue"]} contentStyle={{ borderRadius: "8px", border: "1px solid #E6E4E0", fontSize: 12 }} />
                <Bar dataKey="revenue" fill={tierStyle.accent} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {(p.daily_sales || []).map(d => (
              <div key={d.date} className="text-center">
                <p className="text-[10px] text-slate-400">{d.day}</p>
                <p className="text-xs font-medium text-slate-900">${d.revenue}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-3 gap-3 text-xs">
            <div><span className="text-slate-400">Appts:</span> <span className="font-medium">{p.appointments}</span></div>
            <div><span className="text-slate-400">Avg Ticket:</span> <span className="font-medium">${p.avg_ticket}</span></div>
            <div><span className="text-slate-400">Hours:</span> <span className="font-medium">{p.hours_worked}h</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PayrollPage({ embedded = false }) {
  const [payroll, setPayroll] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 8) + "01");
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [showTierSettings, setShowTierSettings] = useState(false);
  const [tierForm, setTierForm] = useState([]);
  const [tierSaving, setTierSaving] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const locParam = selectedLocation !== "all" ? `&location_id=${selectedLocation}` : "";
      const [res, tiersRes] = await Promise.all([
        api.get(`/payroll/summary?start_date=${startDate}&end_date=${endDate}${locParam}`),
        api.get("/payroll/commission-tiers"),
      ]);
      setPayroll(res.data);
      setTiers(tiersRes.data.tiers || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/locations").then(r => setLocations(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchPayroll(); }, [selectedLocation, startDate, endDate]); // eslint-disable-line

  const openTierSettings = () => {
    setTierForm(tiers.filter(t => t.min_sales > 0).map(t => ({ ...t })));
    setShowTierSettings(true);
  };

  const saveTiers = async () => {
    setTierSaving(true);
    try {
      const allTiers = [...tierForm, { min_sales: 0, rate: 0, label: "Base" }];
      await api.put("/payroll/commission-tiers", { tiers: allTiers });
      toast.success("Commission tiers saved");
      setShowTierSettings(false);
      fetchPayroll();
    } catch { toast.error("Failed to save"); }
    finally { setTierSaving(false); }
  };

  const generateReport = async () => {
    setReportLoading(true);
    try {
      const locParam = selectedLocation !== "all" ? `&location_id=${selectedLocation}` : "";
      const res = await api.get(`/payroll/commission-report?start_date=${startDate}&end_date=${endDate}${locParam}`);
      setReport(res.data);
      setShowReport(true);
    } catch { toast.error("Failed to generate report"); }
    finally { setReportLoading(false); }
  };

  const printReport = () => {
    window.print();
  };

  const totalPay = payroll.reduce((s, p) => s + p.total_pay, 0);
  const totalRevenue = payroll.reduce((s, p) => s + p.service_revenue, 0);
  const totalCommissions = payroll.reduce((s, p) => s + p.commission, 0);
  const totalTips = payroll.reduce((s, p) => s + p.tips, 0);

  return (
    <div className={embedded ? "" : "p-6 lg:p-8 max-w-[1600px] mx-auto"} data-testid="payroll-page">
      <div className={`flex items-center ${embedded ? "justify-end" : "justify-between"} mb-8 animate-fadeIn flex-wrap gap-3`}>
        {!embedded && (
          <div>
            <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Payroll</h1>
            <p className="text-slate-600 text-sm mt-1">Variable commissions, tips, and compensation tracking</p>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={generateReport} disabled={reportLoading} data-testid="generate-report-btn"
            className="border-slate-200 rounded-full h-9 px-4 text-sm">
            <FileText className="w-4 h-4 mr-1.5" />{reportLoading ? "Generating..." : "Commission Report"}
          </Button>
          <Input data-testid="payroll-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 border-slate-200 rounded-lg text-sm w-36" />
          <span className="text-slate-400">to</span>
          <Input data-testid="payroll-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 border-slate-200 rounded-lg text-sm w-36" />
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-[160px] h-9 border-slate-200 bg-white rounded-lg text-sm">
              <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Payroll", value: `$${totalPay.toFixed(0)}`, icon: DollarSign },
          { label: "Service Revenue", value: `$${totalRevenue.toFixed(0)}`, icon: TrendingUp },
          { label: "Commissions", value: `$${totalCommissions.toFixed(0)}`, icon: DollarSign },
          { label: "Total Tips", value: `$${totalTips.toFixed(0)}`, icon: Users },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <card.icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{card.label}</span>
            </div>
            <p className="font-['Outfit'] text-2xl font-medium text-slate-900">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Commission Tiers Bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 flex items-center gap-4" data-testid="commission-tiers-bar">
        <Zap className="w-5 h-5 text-rose-400 flex-shrink-0" strokeWidth={1.5} />
        <div className="flex-1 flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Commission Tiers</span>
          {tiers.filter(t => t.min_sales > 0).map(t => {
            const tc = Object.values(TIER_COLORS)[tiers.filter(ti => ti.min_sales > 0).indexOf(t)] || TIER_COLORS.Base;
            return (
              <span key={t.min_sales} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${tc?.accent || "#8A8A8A"}15`, color: tc?.accent || "#8A8A8A" }}>
                ${t.min_sales}+ → {(t.rate * 100).toFixed(0)}%
              </span>
            );
          })}
        </div>
        <button onClick={openTierSettings} data-testid="edit-tiers-btn"
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-900 transition-colors px-2 py-1 rounded-lg hover:bg-[#FAFAFA]">
          <Settings2 className="w-3.5 h-3.5" />Edit
        </button>
      </div>

      {/* Tier Settings Modal */}
      {showTierSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="tier-settings-modal">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-['Outfit'] text-lg font-medium text-slate-900">Commission Tiers</h2>
              <button onClick={() => setShowTierSettings(false)} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center"><X className="w-4 h-4 text-slate-600" /></button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-xs text-slate-400 mb-2">Set sales thresholds for 7-day rolling periods. Commission rates auto-apply when stylists reach each tier.</p>
              {tierForm.map((t, i) => (
                <div key={t.id || `tier-row-${i}`} className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-400 uppercase">Label</label>
                    <Input value={t.label} onChange={e => { const n = [...tierForm]; n[i].label = e.target.value; setTierForm(n); }}
                      className="h-8 text-sm border-slate-200" />
                  </div>
                  <div className="w-24">
                    <label className="text-[10px] text-slate-400 uppercase">Min Sales $</label>
                    <Input type="number" value={t.min_sales} onChange={e => { const n = [...tierForm]; n[i].min_sales = parseInt(e.target.value) || 0; setTierForm(n); }}
                      className="h-8 text-sm border-slate-200" data-testid={`tier-min-${i}`} />
                  </div>
                  <div className="w-20">
                    <label className="text-[10px] text-slate-400 uppercase">Rate %</label>
                    <Input type="number" value={(t.rate * 100).toFixed(0)} onChange={e => { const n = [...tierForm]; n[i].rate = (parseFloat(e.target.value) || 0) / 100; setTierForm(n); }}
                      className="h-8 text-sm border-slate-200" data-testid={`tier-rate-${i}`} />
                  </div>
                  <button onClick={() => setTierForm(f => f.filter((_, j) => j !== i))} className="mt-4 text-slate-400 hover:text-rose-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setTierForm(f => [...f, { min_sales: 0, rate: 0, label: `Tier ${f.length + 1}` }])}
                className="border-slate-200 rounded-full h-8 text-xs px-3 w-full" data-testid="add-tier-btn">
                <Plus className="w-3 h-3 mr-1" />Add Tier
              </Button>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTierSettings(false)} className="border-slate-200 rounded-full h-9 px-4 text-xs">Cancel</Button>
              <Button onClick={saveTiers} disabled={tierSaving} data-testid="save-tiers-btn"
                className="bg-slate-900 text-white rounded-full h-9 px-5 text-xs hover:bg-slate-800">
                <Check className="w-3 h-3 mr-1" />{tierSaving ? "Saving..." : "Save Tiers"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Provider Cards Grid (commission vs hourly) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8" data-testid="commission-grid">
        {payroll.map(p => p.pay_type === "hourly"
          ? <HourlyCard key={p.user_id} p={p} startDate={startDate} endDate={endDate} onChanged={fetchPayroll} />
          : <CommissionCard key={p.user_id} p={p} tiers={tiers.filter(t => t.min_sales > 0)} startDate={startDate} endDate={endDate} onChanged={fetchPayroll} />
        )}
      </div>

      {/* Full Detail Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="payroll-table">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-slate-200">
              <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Provider</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">7d Sales / Hours</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Tier / Type</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Rate</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Revenue</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Commission / Hourly</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Tips</th>
              <th className="text-right px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Total Pay</th>
            </tr>
          </thead>
          <tbody>
            {payroll.map(p => {
              const isHourly = p.pay_type === "hourly";
              const tierStyle = TIER_COLORS[p.commission_tier] || TIER_COLORS.Base;
              return (
                <tr key={p.user_id} className="border-b border-slate-200 hover:bg-[#FAFAFA] transition-colors" data-testid={`payroll-row-${p.user_id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ backgroundColor: p.color }}>
                        {p.name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{p.name}</p>
                        <p className="text-xs text-slate-400 capitalize">{p.role?.replace("_", " ")}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-right font-medium text-slate-900">
                    {isHourly ? `${(p.clocked_hours ?? 0).toFixed(2)}h` : `$${p.week_sales.toLocaleString()}`}
                  </td>
                  <td className="px-3 py-4 text-center">
                    {isHourly ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-indigo-100 text-indigo-700 uppercase tracking-wider">Hourly</span>
                    ) : (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wider">Commission</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: tierStyle.bg, color: tierStyle.text }}>
                          {p.commission_tier}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-right" style={{ color: isHourly ? "#6366F1" : tierStyle.accent }}>
                    {isHourly ? `$${Number(p.hourly_rate || 0).toFixed(2)}/hr` : `${(p.commission_rate * 100).toFixed(0)}%`}
                  </td>
                  <td className="px-3 py-4 text-sm text-right text-slate-900">${p.service_revenue.toFixed(0)}</td>
                  <td className="px-3 py-4 text-sm text-right text-slate-900">
                    {isHourly ? `$${(p.hourly_pay ?? 0).toFixed(2)}` : `$${p.commission.toFixed(0)}`}
                  </td>
                  <td className="px-3 py-4 text-sm text-right text-emerald-600">${p.tips.toFixed(0)}</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-slate-900">${p.total_pay.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Commission Report Modal */}
      {showReport && report && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="commission-report-modal">
          <div className="w-full max-w-[900px] max-h-[90vh] bg-white rounded-2xl overflow-y-auto shadow-2xl">
            {/* Report Header */}
            <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-8 py-4 flex items-center justify-between print:px-0 print:border-0">
              <div>
                <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">Commission Payout Report</h2>
                <p className="text-xs text-slate-400 mt-0.5">{report.period.start} to {report.period.end}</p>
              </div>
              <div className="flex items-center gap-2 print:hidden">
                <Button variant="outline" size="sm" onClick={printReport} data-testid="print-report-btn"
                  className="border-slate-200 rounded-full h-8 px-4 text-xs">
                  <Printer className="w-3 h-3 mr-1.5" />Print
                </Button>
                <button onClick={() => setShowReport(false)} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center">
                  <X className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>

            {/* Tiers Reference */}
            <div className="px-8 py-3 bg-[#FAFAFA] border-b border-slate-200 flex items-center gap-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Active Tiers</span>
              {(report.tiers || []).filter(t => t.min_sales > 0).map(t => (
                <span key={t.min_sales} className="text-xs text-slate-600">{t.label}: ${t.min_sales}+ @ {(t.rate * 100).toFixed(0)}%</span>
              ))}
            </div>

            {/* Per-Stylist Reports */}
            <div className="p-8 space-y-8">
              {(report.reports || []).map(r => {
                const tierStyle = TIER_COLORS[r.commission_tier] || TIER_COLORS.Base;
                return (
                  <div key={r.user_id} className="border border-slate-200 rounded-2xl overflow-hidden" data-testid={`report-stylist-${r.user_id}`}>
                    {/* Stylist Header */}
                    <div className="bg-[#FAFAFA] px-6 py-4 flex items-center justify-between border-b border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ backgroundColor: r.color }}>
                          {r.name?.[0]}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                          <p className="text-xs text-slate-400 capitalize">{r.role?.replace("_", " ")}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-right">
                        <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: tierStyle.bg, color: tierStyle.text }}>
                          {r.commission_tier} — {(r.commission_rate * 100).toFixed(0)}%
                        </span>
                        <div>
                          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">${r.total_payout.toFixed(2)}</p>
                          <p className="text-[10px] text-slate-400">Total Payout</p>
                        </div>
                      </div>
                    </div>

                    {/* Service Line Items */}
                    {r.service_items.length > 0 && (
                      <div className="px-6 py-3">
                        <table className="w-full">
                          <thead>
                            <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                              <th className="text-left py-2">Date</th>
                              <th className="text-left py-2">Service</th>
                              <th className="text-right py-2">Price</th>
                              <th className="text-right py-2">Qty</th>
                              <th className="text-right py-2">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.service_items.map((item, idx) => (
                              <tr key={`${r.user_id || ""}-svc-${item.date || ""}-${idx}`} className="border-t border-slate-200/50">
                                <td className="py-2 text-xs text-slate-400">{item.date}</td>
                                <td className="py-2 text-xs text-slate-900">{item.name}</td>
                                <td className="py-2 text-xs text-right text-slate-600">${item.price.toFixed(2)}</td>
                                <td className="py-2 text-xs text-right text-slate-600">{item.qty}</td>
                                <td className="py-2 text-xs text-right font-medium text-slate-900">${item.total.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Payout Summary */}
                    <div className="bg-[#FAFAFA] px-6 py-4 border-t border-slate-200">
                      <div className="grid grid-cols-5 gap-4 text-center">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Services</p>
                          <p className="text-sm font-medium text-slate-900">{r.total_services}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Service Revenue</p>
                          <p className="text-sm font-medium text-slate-900">${r.service_revenue.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Commission ({(r.commission_rate * 100).toFixed(0)}%)</p>
                          <p className="text-sm font-semibold text-slate-900">${r.commission.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Tips</p>
                          <p className="text-sm font-medium text-emerald-600">${r.tips.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Payout</p>
                          <p className="text-sm font-bold text-slate-900">${r.total_payout.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Grand Total */}
              <div className="border-t-2 border-rose-500 pt-4 flex items-center justify-between">
                <p className="text-sm font-bold uppercase tracking-wider text-slate-900">Grand Total Payout</p>
                <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">
                  ${(report.reports || []).reduce((s, r) => s + r.total_payout, 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
